import * as React from 'react';
import { PopupThreeLevelLocationPickerProps } from '..';
import { t } from 'i18n';
import { ModalHeader, ModalBody, Modal } from '@teko/ui';
import { useLevelThreeLevelLocation } from 'hooks/useThreeLevelLocation';
import {
  CheckOutlined,
  BackOutlined,
  CloseOutlined,
  SearchOutlined,
} from '@teko/ds-icon';
import {
  styled,
  Body,
  Row,
  Input,
  Loading as BaseLoading,
  useTheme,
} from '@teko/ds-desktop';
import { useRef } from 'react';
import { DISTRICT, PROVINCE, WARD } from 'constants/threeLevelLocation';

export const PopupThreeLevelLocationPickerDesktop: React.FC<
  Omit<PopupThreeLevelLocationPickerProps, 'isMobile'>
> = ({
  onClosePopup,
  showPopup,
  threeLevelLocation,
  updateThreeLevelLocation,
  stepLocation,
  setStepLocation,
  onSubmitNewLocation,
  correctThreeLevelLocation,
}) => {
  const listRef = useRef<HTMLDivElement>();
  const displayBackButton = stepLocation === DISTRICT || stepLocation === WARD;
  const theme = useTheme();

  const {
    filterLocations,
    loadingLocation,
    onSelectLocation,
    backToPreviousLevelLocation,
    headerText,
    keyword,
    setKeyword,
  } = useLevelThreeLevelLocation({
    threeLevelLocation,
    stepLocation,
    updateThreeLevelLocation,
    setStepLocation,
    onClosePopup,
    onSubmitNewLocation,
  });
  return (
    <Modal show={showPopup} onHide={onClosePopup}>
      {displayBackButton && (
        <LeftIcon onClick={backToPreviousLevelLocation}>
          <BackOutlined size={32} color={theme.colors.black} />
        </LeftIcon>
      )}
      <ModalHeader>{headerText.title}</ModalHeader>
      {!!correctThreeLevelLocation && (
        <CloseIcon onClick={onClosePopup}>
          <CloseOutlined size={32} color={theme.colors.black} />
        </CloseIcon>
      )}

      <ModalBody>
        <Wrapper>
          <div style={{ marginBottom: 12, textAlign: 'center' }}>
            <Body level={1}>{headerText.sutTitle}</Body>
          </div>
          <DefaultContent>
            <Row>
              <StyledInput
                prefix={<SearchOutlined />}
                placeholder={headerText.placeHolderSearch}
                value={keyword}
                onChange={setKeyword}
              />
            </Row>
            <List
              ref={(ref) => {
                if (!ref) {
                  return;
                }
                // remove right padding when scrollbar is visible
                if (ref.scrollHeight > ref.clientHeight) {
                  ref.style.paddingRight = '0';
                }

                listRef.current = ref;
              }}
            >
              {filterLocations &&
              Array.isArray(filterLocations) &&
              filterLocations.length ? (
                filterLocations.map((location) => {
                  const isSelected =
                    (stepLocation === PROVINCE &&
                      location.value === threeLevelLocation.province.code) ||
                    (stepLocation === DISTRICT &&
                      location.value === threeLevelLocation.district?.code) ||
                    (stepLocation === WARD &&
                      location.value === threeLevelLocation.ward.code);
                  return (
                    <ListItem
                      key={location.value}
                      onClick={() => {
                        onSelectLocation({
                          code: location.value,
                          label: location.label,
                          id: location.id,
                        });
                        setKeyword('');
                      }}
                      active={isSelected}
                    >
                      {location.label}
                      {isSelected && (
                        <CheckOutlined color="primary500" size="1.5rem" />
                      )}
                    </ListItem>
                  );
                })
              ) : (
                <Row justify="center" align="middle">
                  {loadingLocation ? (
                    <BaseLoading isLoading={loadingLocation} />
                  ) : (
                    <Body color="placeholder" level={1} weight="regular">
                      {t('Không tìm thấy dữ liệu nào !!')}
                    </Body>
                  )}
                </Row>
              )}
            </List>
          </DefaultContent>
        </Wrapper>
      </ModalBody>
    </Modal>
  );
};

const CloseIcon = styled.div`
  z-index: 1060;
  position: absolute;
  top: 12px;
  right: 12px;
  width: 30px;
  height: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const LeftIcon = styled.div`
  z-index: 1060;
  position: absolute;
  top: 12px;
  left: 12px;
  width: 30px;
  height: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const Wrapper = styled.div`
  text-align: center;
`;

const DefaultContent = styled.div`
  min-height: 60vh;
  text-align: center;
  color: #000000;
`;

const StyledInput = styled(Input)`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.background};
  padding: 0 1rem;
  margin-bottom: 15px;
  border-radius: 4px;
  .input-container {
    border: none;
  }
`;

const List = styled.div`
  overflow-x: hidden;
  overflow-y: auto;
  max-height: min(100vh, 28rem);
  padding-bottom: 0.75rem;
  ::-webkit-scrollbar {
    width: 0.75rem;
  }

  ::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.disable};
    background-clip: content-box;
    border-radius: 100px;
    border: 0.25rem solid transparent;
    width: 0.25rem;
  }

  ::-webkit-scrollbar-thumb:hover {
    background-color: ${({ theme }) => theme.colors.placeholder};
  }
`;

const ListItem = styled.div<{ active?: boolean }>`
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
  display: flex;
  flex-direction: row;
  height: 3rem;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: 15px;
  background-color: ${({ theme, active }) =>
    active ? theme.colors.background : theme.colors.white};
`;
